import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getApplicationRoleById, type ApplicationRole } from './availableRolesSlice';

export interface Wallet {
  availableBalance: number;
  inOrderBalance: number;
  withdrawPendingBalance: number;
  totalBalance: number;
}

export interface UserResponse {
  userid: number;
  refname: string;
  roleid: number;
  rolename: ApplicationRole;
  isActive: boolean;
  userstatus: number;
  wallet: Wallet | null;
  isforpwdchange: boolean;
  statusname?: string;

  // Compatibility aliases used by existing admin-panel components.
  id: string;
  username: string;
  status: string;
  roleId: number;
  role: {
    id: number;
    name: ApplicationRole;
  };
  passwordChanged: boolean;
  partnership?: number;
}

export interface UserProfileApiData {
  userid: number;
  username: string;
  refname: string;
  roleid: number;
  transaction_code: string;
  isActive: boolean;
  userstatus: number;
  wallet: Wallet;
}

export interface UserProfileApiResponse {
  success: boolean;
  message: string;
  data: UserProfileApiData;
}

export interface UserState {
  data: UserResponse | null | undefined;
}

const initialState: UserState = {
  data: null,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<UserResponse>) {
      state.data = action.payload;
    },
    updateUserProfile(state, action: PayloadAction<UserProfileApiData>) {
      const profile = action.payload;
      const roleName = state.data?.rolename ?? getApplicationRoleById(profile.roleid) ?? 'User';

      if (!state.data) {
        state.data = {
          ...profile,
          rolename: roleName,
          isforpwdchange: false,
          id: String(profile.userid),
          status: profile.isActive ? 'Active' : 'Inactive',
          roleId: profile.roleid,
          role: { id: profile.roleid, name: roleName },
          passwordChanged: true,
        };
        return;
      }

      state.data = {
        ...state.data,
        ...profile,
        id: String(profile.userid),
        roleId: profile.roleid,
      };
    },
    clearUser(state) {
      state.data = null;
    },
  },
});

export const { setUser, updateUserProfile, clearUser } = userSlice.actions;
export const userReducer = userSlice.reducer;
